import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { buildEcommerceItem, trackEcommerceEvent, getSafeCurrencyFromItem, getSafePriceFromItem } from '@/utils/analytics';
import {
  isFootballPackage,
  isGemPackage,
  validateRetroFootballCartAddition
} from '@/data/retroFootball';

/**
 * Cart Context - Simple cart management
 *
 * Handles local cart state only.
 * CheckoutContext handles all Tebex basket operations.
 */
const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const STORAGE_KEYS = {
  CART: 'retrostore_cart'
};

export const CartProvider = ({ children }) => {
  // Initialize cart from localStorage
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem(STORAGE_KEYS.CART);
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });
  
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.CART, JSON.stringify(cartItems));
      // NOTE: Do NOT clear basket ident here - it breaks auth return flow
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error);
    }
  }, [cartItems]);

  // Check if product is already in cart
  const isInCart = useCallback((productId) => {
    return cartItems.some(item => item.id === productId);
  }, [cartItems]);

  // Add item to cart
  const addToCart = useCallback((product) => {
    const validation = validateRetroFootballCartAddition(cartItems, product);
    if (!validation.valid) {
      toast.error(validation.message);
      return false;
    }

    const wasEmpty = cartItems.length === 0;
    let didAdd = false;

    const cartItemId = product.giftTo
      ? `${product.id}_gift_${product.giftTo}`
      : product.id;

    setCartItems(prevItems => {
      if (product.giftTo) {
        const existingGift = prevItems.find(
          item => item.id === product.id && item.giftTo === product.giftTo
        );
        if (existingGift) return prevItems;
        didAdd = true;
        return [...prevItems, { ...product, cartItemId, quantity: 1 }];
      }

      const existingItem = prevItems.find(item => item.id === product.id && !item.giftTo);
      if (existingItem) return prevItems;
      didAdd = true;
      return [...prevItems, { ...product, cartItemId, quantity: 1 }];
    });

    if (didAdd) {
      const price = getSafePriceFromItem(product);
      trackEcommerceEvent('add_to_cart', {
        currency: getSafeCurrencyFromItem(product),
        value: price,
        items: [buildEcommerceItem(product, 1)]
      });
    }

    // Auto-open cart when going from 0 to 1 items
    if (wasEmpty) {
      setTimeout(() => setIsCartOpen(true), 1100);
    }

    return true;
  }, [cartItems]);

  // Remove item from cart
  const removeFromCart = useCallback((itemId) => {
    setCartItems(prevItems => {
      const itemToRemove = prevItems.find(item => (item.cartItemId || item.id) === itemId);

      if (itemToRemove) {
        const quantity = itemToRemove.quantity || 1;
        const price = getSafePriceFromItem(itemToRemove);
        trackEcommerceEvent('remove_from_cart', {
          currency: getSafeCurrencyFromItem(itemToRemove),
          value: price * quantity,
          items: [buildEcommerceItem(itemToRemove, quantity)]
        });
      }

      return prevItems.filter(item => (item.cartItemId || item.id) !== itemId);
    });
  }, []);

  // Update item quantity
  const updateQuantity = useCallback((itemId, newQuantity) => {
    const currentItem = cartItems.find(
      item => (item.cartItemId || item.id) === itemId
    );

    if (currentItem && isFootballPackage(currentItem) && Number(newQuantity) !== 1) {
      toast.error('Retro Fantasy Football quantity must be exactly one.');
      return false;
    }

    if (
      currentItem &&
      isGemPackage(currentItem) &&
      (!Number.isInteger(Number(newQuantity)) || Number(newQuantity) < 1)
    ) {
      toast.error('Fantasy Gem quantity must be a positive whole number.');
      return false;
    }

    if (newQuantity <= 0) {
      removeFromCart(itemId);
    } else {
      setCartItems(prevItems =>
        prevItems.map(item =>
          (item.cartItemId || item.id) === itemId
            ? { ...item, quantity: Number(newQuantity) }
            : item
        )
      );
    }
    return true;
  }, [cartItems, removeFromCart]);

  // Get total price (uses numeric basePrice for reliable calculation)
  const getTotalPrice = useCallback(() => {
    return cartItems.reduce((total, item) => {
      const price = item.basePrice || 0;
      return total + (price * (item.quantity || 1));
    }, 0).toFixed(2);
  }, [cartItems]);

  // Get currency from cart items (null if empty)
  const getCartCurrency = useCallback(() => {
    if (cartItems.length === 0) return null;
    return cartItems[0].currency || 'USD';
  }, [cartItems]);

  // Get total items count
  const getTotalItems = useCallback(() => {
    return cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
  }, [cartItems]);

  // Clear cart
  const clearCart = useCallback(() => {
    setCartItems([]);
    localStorage.removeItem('tebex_basket_ident');
    localStorage.removeItem('tebex_basket_ready');
    localStorage.removeItem('tebex_basket_packages');
  }, []);

  // Validate cart currency against current products currency
  // Called by ProductsContext after products are fetched
  const validateCartCurrency = useCallback((productsCurrency) => {
    if (cartItems.length === 0) return; // Cart is empty, nothing to validate

    const cartCurrency = cartItems[0]?.currency || 'USD';
    if (cartCurrency !== productsCurrency) {
      // Currency mismatch - clear cart and notify user
      setCartItems([]);
      localStorage.removeItem('tebex_basket_ident');
      localStorage.removeItem('tebex_basket_ready');
      localStorage.removeItem('tebex_basket_packages');
      toast.info('Your cart was cleared due to a currency change');
    }
  }, [cartItems]);

  const value = {
    cartItems,
    setCartItems,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    getTotalPrice,
    getTotalItems,
    clearCart,
    isInCart,
    getCartCurrency,
    validateCartCurrency
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
